/**
 * Status Mapping
 * Fixed processing steps for file and document status to UI state mapping
 *
 * Replaces the old client-workflows.ts with a simpler, fixed approach
 */
import type { UploadStatus } from "@/components/application/upload-status/upload-status-card";
import { DocumentStatusEnum } from "@/types/api/documents";
import { FileStatusEnum } from "@/types/api/files";

// =============================================================================
// FILE STATUS MAPPING
// =============================================================================

/**
 * File status progress configuration
 */
export interface FileStatusConfig {
    status: FileStatusEnum | "uploading";
    progress: number;
    uiState: UploadStatus;
    label: string;
    isFinal: boolean;
}

/**
 * File status to UI mapping
 */
export const FILE_STATUS_CONFIG: Record<string, FileStatusConfig> = {
    // Local upload (before API call)
    uploading: {
        status: "uploading",
        progress: 0,
        uiState: "uploading",
        label: "Uploading...",
        isFinal: false,
    },
    // Pending - file uploaded, waiting for processing
    [FileStatusEnum.Pending]: {
        status: FileStatusEnum.Pending,
        progress: 20,
        uiState: "queued",
        label: "Queued...",
        isFinal: false,
    },
    // Uploaded - file uploaded (same as pending for UI purposes)
    [FileStatusEnum.Uploaded]: {
        status: FileStatusEnum.Uploaded,
        progress: 20,
        uiState: "queued",
        label: "Uploaded",
        isFinal: false,
    },
    // OCR - text extraction in progress
    [FileStatusEnum.OCR]: {
        status: FileStatusEnum.OCR,
        progress: 40,
        uiState: "processing",
        label: "Reading document...",
        isFinal: false,
    },
    // Classification - document type identification and splitting
    [FileStatusEnum.Classification]: {
        status: FileStatusEnum.Classification,
        progress: 60,
        uiState: "processing",
        label: "Classifying document...",
        isFinal: false,
    },
    // Processing - parsing/data extraction
    [FileStatusEnum.Processing]: {
        status: FileStatusEnum.Processing,
        progress: 70,
        uiState: "processing",
        label: "Processing...",
        isFinal: false,
    },
    // Completed - file processing done, check documents
    [FileStatusEnum.Completed]: {
        status: FileStatusEnum.Completed,
        progress: 100,
        uiState: "success",
        label: "Completed",
        isFinal: true,
    },
    // Failed - file processing failed
    [FileStatusEnum.Failed]: {
        status: FileStatusEnum.Failed,
        progress: 100,
        uiState: "error",
        label: "Processing failed",
        isFinal: true,
    },
    // Deleted - file was deleted
    [FileStatusEnum.Deleted]: {
        status: FileStatusEnum.Deleted,
        progress: 100,
        uiState: "error",
        label: "Deleted",
        isFinal: true,
    },
};

// =============================================================================
// DOCUMENT STATUS MAPPING
// =============================================================================

/**
 * Document status progress configuration
 */
export interface DocumentStatusConfig {
    status: DocumentStatusEnum;
    progress: number;
    uiState: UploadStatus;
    label: string;
    isFinal: boolean;
    isSuccess: boolean;
    isError: boolean;
}

/**
 * Document status to UI mapping
 */
export const DOCUMENT_STATUS_CONFIG: Record<DocumentStatusEnum, DocumentStatusConfig> = {
    // Pending - document being processed
    [DocumentStatusEnum.Pending]: {
        status: DocumentStatusEnum.Pending,
        progress: 70,
        uiState: "processing",
        label: "Extracting data...",
        isFinal: false,
        isSuccess: false,
        isError: false,
    },
    // Parsed - document successfully processed
    [DocumentStatusEnum.Parsed]: {
        status: DocumentStatusEnum.Parsed,
        progress: 100,
        uiState: "success",
        label: "Ready for export",
        isFinal: true,
        isSuccess: true,
        isError: false,
    },
    // Queued - document queued for export
    [DocumentStatusEnum.Queued]: {
        status: DocumentStatusEnum.Queued,
        progress: 100,
        uiState: "success",
        label: "Queued for export",
        isFinal: true,
        isSuccess: true,
        isError: false,
    },
    // Exported - document has been exported
    [DocumentStatusEnum.Exported]: {
        status: DocumentStatusEnum.Exported,
        progress: 100,
        uiState: "exported",
        label: "Exported",
        isFinal: true,
        isSuccess: true,
        isError: false,
    },
    // Rejected - document was rejected
    [DocumentStatusEnum.Rejected]: {
        status: DocumentStatusEnum.Rejected,
        progress: 100,
        uiState: "error",
        label: "Rejected",
        isFinal: true,
        isSuccess: false,
        isError: true,
    },
    // Error - processing error occurred
    [DocumentStatusEnum.Error]: {
        status: DocumentStatusEnum.Error,
        progress: 100,
        uiState: "error",
        label: "Error",
        isFinal: true,
        isSuccess: false,
        isError: true,
    },
    // Deleted - document was deleted
    [DocumentStatusEnum.Deleted]: {
        status: DocumentStatusEnum.Deleted,
        progress: 100,
        uiState: "error",
        label: "Deleted",
        isFinal: true,
        isSuccess: false,
        isError: true,
    },
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get file status configuration
 */
export function getFileStatusConfig(status: FileStatusEnum | "uploading"): FileStatusConfig {
    return FILE_STATUS_CONFIG[status] || FILE_STATUS_CONFIG[FileStatusEnum.Pending];
}

/**
 * Get document status configuration
 */
export function getDocumentStatusConfig(status: DocumentStatusEnum): DocumentStatusConfig {
    return DOCUMENT_STATUS_CONFIG[status] || DOCUMENT_STATUS_CONFIG[DocumentStatusEnum.Pending];
}

/**
 * Get progress percentage for file status
 */
export function getFileProgress(status: FileStatusEnum | "uploading"): number {
    return getFileStatusConfig(status).progress;
}

/**
 * Get progress percentage for document status
 */
export function getDocumentProgress(status: DocumentStatusEnum): number {
    return getDocumentStatusConfig(status).progress;
}

/**
 * Get UI state for file status
 */
export function getFileUiState(status: FileStatusEnum | "uploading"): UploadStatus {
    return getFileStatusConfig(status).uiState;
}

/**
 * Get UI state for document status
 */
export function getDocumentUiState(status: DocumentStatusEnum): UploadStatus {
    return getDocumentStatusConfig(status).uiState;
}

/**
 * Get label for file status
 */
export function getFileStatusLabel(status: FileStatusEnum | "uploading"): string {
    return getFileStatusConfig(status).label;
}

/**
 * Get label for document status
 * Supports optional detailed processing steps
 */
export function getDocumentStatusLabel(status: DocumentStatusEnum, isProcessingEndStateReached?: boolean, currentStepCode?: string | null): string {
    // If document is Parsed but hasn't reached end state, show detailed step
    if (status === DocumentStatusEnum.Parsed && isProcessingEndStateReached === false) {
        switch (currentStepCode) {
            case "vendor_lookup":
                return "Identifying Vendor...";
            case "receipt_lookup":
                return "Finding Receipt(s)...";
            case "match":
                return "Matching Document(s)...";
            default:
                return "Processing...";
        }
    }

    return getDocumentStatusConfig(status).label;
}

/**
 * Check if file status is final (stop polling)
 */
export function isFileStatusFinal(status: FileStatusEnum | "uploading"): boolean {
    return getFileStatusConfig(status).isFinal;
}

/**
 * Check if document status is final (stop polling)
 */
export function isDocumentStatusFinal(status: DocumentStatusEnum): boolean {
    return getDocumentStatusConfig(status).isFinal;
}

/**
 * Check if document status is a success state
 */
export function isDocumentStatusSuccess(status: DocumentStatusEnum): boolean {
    return getDocumentStatusConfig(status).isSuccess;
}

/**
 * Check if document status is an error state
 */
export function isDocumentStatusError(status: DocumentStatusEnum): boolean {
    return getDocumentStatusConfig(status).isError;
}

// =============================================================================
// END STATES
// =============================================================================

/**
 * File statuses that indicate processing is complete (stop file polling)
 */
export const FILE_END_STATES: FileStatusEnum[] = [FileStatusEnum.Completed, FileStatusEnum.Failed, FileStatusEnum.Deleted];

/**
 * Document statuses that indicate processing is complete (stop document polling)
 */
export const DOCUMENT_END_STATES: DocumentStatusEnum[] = [
    DocumentStatusEnum.Parsed,
    DocumentStatusEnum.Queued,
    DocumentStatusEnum.Exported,
    DocumentStatusEnum.Rejected,
    DocumentStatusEnum.Error,
    DocumentStatusEnum.Deleted,
];

/**
 * Document statuses that indicate success
 */
export const DOCUMENT_SUCCESS_STATES: DocumentStatusEnum[] = [DocumentStatusEnum.Parsed, DocumentStatusEnum.Queued, DocumentStatusEnum.Exported];

/**
 * Document statuses that indicate error
 */
export const DOCUMENT_ERROR_STATES: DocumentStatusEnum[] = [DocumentStatusEnum.Rejected, DocumentStatusEnum.Error, DocumentStatusEnum.Deleted];
